import React, { useState, useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Login from './components/Login';
import CharacterSelector from './components/CharacterSelector';
import GameDashboard from './components/GameDashboard';
import TermsOfService from './components/TermsOfService';
import config, { getApiUrl } from './config';
import { UGCProvider } from './contexts/UGCContext';

const theme = createTheme({
  palette: {
    primary: {
      main: '#667eea',
    },
    secondary: {
      main: '#764ba2',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 500,
    },
  },
});

function App() {
  const [currentView, setCurrentView] = useState('login'); // 'login', 'character-select', 'game', 'tos'
  const [authData, setAuthData] = useState(null); // Authentication data from login
  const [sessionData, setSessionData] = useState(null); // Session data after character selection

  // Check if URL path is /tos and redirect to the raw text file
  useEffect(() => {
    if (window.location.pathname === '/tos') {
      // Redirect to the raw tos.txt file
      window.location.href = '/tos.txt';
    }
  }, []);

  // Load session data from localStorage on app start
  useEffect(() => {
    // Don't load session if we're on the /tos page
    if (window.location.pathname === '/tos') {
      return;
    }

    const savedSessionData = localStorage.getItem(config.app.storage.sessionKey);
    const savedAuthData = localStorage.getItem(config.app.storage.authKey);

    if (savedSessionData && savedAuthData) {
      try {
        const parsedSessionData = JSON.parse(savedSessionData);
        const parsedAuthData = JSON.parse(savedAuthData);

        // Check if the session is still valid (not expired)
        if (parsedSessionData.token) {
          setAuthData(parsedAuthData);
          setSessionData(parsedSessionData);
          setCurrentView('character-select');
        }
      } catch (err) {
        // Clear invalid data
        localStorage.removeItem(config.app.storage.sessionKey);
        localStorage.removeItem(config.app.storage.authKey);
      }
    }
  }, []);

  // Save session data to localStorage whenever it changes
  useEffect(() => {
    if (sessionData && authData) {
      localStorage.setItem(config.app.storage.sessionKey, JSON.stringify(sessionData));
      localStorage.setItem(config.app.storage.authKey, JSON.stringify(authData));
    }
  }, [sessionData, authData]);

  const refreshCharacterData = async (updatedSessionData = null) => {
    // If we have updated session data passed in, use it directly
    if (updatedSessionData) {
      setSessionData(updatedSessionData);
      return;
    }

    // Note: We no longer re-fetch character data since we removed password storage
    // Character data will be refreshed on next login
    // If needed in the future, create a dedicated refresh endpoint that accepts session token
  };

  const handleLogin = (loginResult) => {
    setAuthData(loginResult);
    setCurrentView('character-select');
  };

  const handleCharacterSelect = (selectionResult) => {
    // Find the character data from authData to ensure we have profile picture info
    const character = authData.displayNames.find(dn => dn.name === selectionResult.selectedDisplayName);
    const updatedSelectionResult = {
      ...selectionResult,
      character: character || selectionResult.character
    };
    
    setSessionData(updatedSelectionResult);
    setCurrentView('game');
  };

  const handleLogout = () => {
    setAuthData(null);
    setSessionData(null);
    setCurrentView('login');
    
    // Clear localStorage
    localStorage.removeItem(config.app.storage.sessionKey);
    localStorage.removeItem(config.app.storage.authKey);
  };

  const handleBackToCharacterSelect = () => {
    // Refresh character data when going back to character select
    refreshCharacterData();
    setSessionData(null);
    setCurrentView('character-select');
    
    // Clear session from localStorage but keep auth data
    localStorage.removeItem(config.app.storage.sessionKey);
  };

  const handleBackFromTos = () => {
    window.history.pushState(null, '', '/');
    setCurrentView('login');
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case 'login':
        return <Login onLogin={handleLogin} />;
      case 'character-select':
        return (
          <CharacterSelector
            authData={authData}
            onLogout={handleLogout}
            onSelectCharacter={handleCharacterSelect}
          />
        );
      case 'game':
        return (
          <GameDashboard
            authData={authData}
            sessionData={sessionData}
            onBack={handleBackToCharacterSelect}
            onLogout={handleLogout}
            onRefreshCharacterData={refreshCharacterData}
          />
        );
      case 'tos':
        return <TermsOfService onBack={handleBackFromTos} />;
      default:
        return <Login onLogin={handleLogin} />;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <UGCProvider sessionData={sessionData}>
        {renderCurrentView()}
      </UGCProvider>
    </ThemeProvider>
  );
}

export default App;
